/**
 * Expand compressed tile selection format to individual tile IDs.
 * Compressed format includes: bundles (#id), ranges (P-Q39-41), individual tiles (A1).
 *
 * @param {Array} tileSelection - Array of compressed tile selection IDs
 * @param {Array} normalizedBundles - Bundle definitions from buildGrid
 * @returns {Array} Array of individual tile IDs
 */
export function expandTileSelection( tileSelection, normalizedBundles = [] ) {
	const tileIds = [];

	for ( const id of tileSelection ) {
		if ( id.startsWith( '#' ) ) {
			// Bundle ID: try to expand
			const bundleId = id.slice( 1 );
			const bundle = normalizedBundles.find( ( b ) => b.id === bundleId );
			if ( bundle ) {
				tileIds.push( ...bundle.tile_ids );
			} else {
				// Bundle not found in normalizedBundles — tiles cannot be marked occupied
				console.warn( `Bundle ${bundleId} not found in normalizedBundles` );
			}
		} else if ( id.includes( '-' ) ) {
			// Range format: "P-Q39-41" or "P39-41"
			const rangeMatch = id.match( /^([A-Z]+)(?:-([A-Z]+))?(\d+)-(\d+)$/i );
			if ( rangeMatch ) {
				const startCol = rangeMatch[ 1 ].toUpperCase();
				const endCol = rangeMatch[ 2 ] ? rangeMatch[ 2 ].toUpperCase() : startCol;
				const startRow = parseInt( rangeMatch[ 3 ] );
				const endRow = parseInt( rangeMatch[ 4 ] );

				const startColNum = colNameToNum( startCol );
				const endColNum = colNameToNum( endCol );

				for ( let c = startColNum; c <= endColNum; c++ ) {
					for ( let r = startRow; r <= endRow; r++ ) {
						tileIds.push( numToColName( c ) + r );
					}
				}
			} else {
				// Single row range: "P39-41"
				const rowRangeMatch = id.match( /^([A-Z]+)(\d+)-(\d+)$/i );
				if ( rowRangeMatch ) {
					const col = rowRangeMatch[ 1 ].toUpperCase();
					const from = parseInt( rowRangeMatch[ 2 ] );
					const to = parseInt( rowRangeMatch[ 3 ] );

					for ( let r = from; r <= to; r++ ) {
						tileIds.push( col + r );
					}
				} else {
					tileIds.push( id );
				}
			}
		} else {
			// Individual tile ID
			tileIds.push( id );
		}
	}

	return tileIds;
}

/**
 * Convert column name (A, B, ..., Z, AA, AB, ...) to number (1, 2, ..., 26, 27, 28, ...).
 * Helper for tile selection expansion.
 */
function colNameToNum( name ) {
	let num = 0;
	for ( let i = 0; i < name.length; i++ ) {
		num = num * 26 + ( name.charCodeAt( i ) - 64 );
	}
	return num;
}

/**
 * Convert column number (1, 2, ..., 26, 27, ...) to name (A, B, ..., Z, AA, AB, ...).
 */
function numToColName( num ) {
	let name = '';
	while ( num > 0 ) {
		num--;
		name = String.fromCharCode( 65 + ( num % 26 ) ) + name;
		num = Math.floor( num / 26 );
	}
	return name;
}
